/**
 * Global utilities and helper functions
 */

// Debounce function
function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction() {
    const context = this;
    const args = arguments;
    const later = function() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

// Throttle function
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Format money
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  
  const value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  
  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    
    if (isNaN(number) || number === null) {
      return '0';
    }
    
    number = (number / 100.0).toFixed(precision);
    
    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/([0-9])(?=([0-9]{3})+(?![0-9]))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';
    
    return dollarsAmount + centsAmount;
  }
  
  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'");
      break;
  }
  
  return formatString.replace(placeholderRegex, value);
}

// Get URL parameters
function getUrlParameter(name) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

// Cookie utilities
const CookieUtil = {
  get: function(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },
  
  set: function(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + value + expires + '; path=/';
  },
  
  remove: function(name) {
    this.set(name, '', -1);
  }
};

// Local storage utilities
const Storage = {
  get: function(key) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (e) {
      return localStorage.getItem(key);
    }
  },
  
  set: function(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      localStorage.setItem(key, value);
    }
  },
  
  remove: function(key) {
    localStorage.removeItem(key);
  },
  
  clear: function() {
    localStorage.clear();
  }
};

// Device detection
const Device = {
  isMobile: function() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },
  
  isTablet: function() {
    return /iPad|Android|webOS|BlackBerry/i.test(navigator.userAgent);
  },
  
  isDesktop: function() {
    return !this.isMobile() && !this.isTablet();
  },
  
  isIOS: function() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  },
  
  isAndroid: function() {
    return /Android/.test(navigator.userAgent);
  }
};

// Animation utilities
const Animation = {
  fadeIn: function(element, duration = 300) {
    element.style.opacity = 0;
    element.style.display = 'block';
    
    const start = performance.now();
    
    const animate = (timestamp) => {
      const elapsed = timestamp - start;
      const progress = elapsed / duration;
      
      if (progress < 1) {
        element.style.opacity = progress;
        requestAnimationFrame(animate);
      } else {
        element.style.opacity = 1;
      }
    };
    
    requestAnimationFrame(animate);
  },
  
  fadeOut: function(element, duration = 300) {
    element.style.opacity = 1;
    
    const start = performance.now();
    
    const animate = (timestamp) => {
      const elapsed = timestamp - start;
      const progress = elapsed / duration;
      
      if (progress < 1) {
        element.style.opacity = 1 - progress;
        requestAnimationFrame(animate);
      } else {
        element.style.opacity = 0;
        element.style.display = 'none';
      }
    };
    
    requestAnimationFrame(animate);
  },
  
  slideUp: function(element, duration = 300) {
    element.style.transitionProperty = 'height, margin, padding';
    element.style.transitionDuration = duration + 'ms';
    element.style.boxSizing = 'border-box';
    element.style.height = element.offsetHeight + 'px';
    element.offsetHeight;
    element.style.overflow = 'hidden';
    element.style.height = 0;
    element.style.paddingTop = 0;
    element.style.paddingBottom = 0;
    element.style.marginTop = 0;
    element.style.marginBottom = 0;
    
    setTimeout(() => {
      element.style.display = 'none';
      element.style.removeProperty('height');
      element.style.removeProperty('padding-top');
      element.style.removeProperty('padding-bottom');
      element.style.removeProperty('margin-top');
      element.style.removeProperty('margin-bottom');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
    }, duration);
  },
  
  slideDown: function(element, duration = 300) {
    element.style.removeProperty('display');
    let display = window.getComputedStyle(element).display;
    
    if (display === 'none') {
      display = 'block';
    }
    
    element.style.display = display;
    const height = element.offsetHeight;
    element.style.overflow = 'hidden';
    element.style.height = 0;
    element.style.paddingTop = 0;
    element.style.paddingBottom = 0;
    element.style.marginTop = 0;
    element.style.marginBottom = 0;
    element.offsetHeight;
    element.style.boxSizing = 'border-box';
    element.style.transitionProperty = 'height, margin, padding';
    element.style.transitionDuration = duration + 'ms';
    element.style.height = height + 'px';
    element.style.removeProperty('padding-top');
    element.style.removeProperty('padding-bottom');
    element.style.removeProperty('margin-top');
    element.style.removeProperty('margin-bottom');
    
    setTimeout(() => {
      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
    }, duration);
  }
};

// Product Card Star Sync Function
async function syncProductCardStars() {
  const productCards = document.querySelectorAll('[data-product-stars]');
  console.log(`Found ${productCards.length} product cards to sync`);
  
  for (const card of productCards) {
    const productId = card.getAttribute('data-product-id');
    if (!productId) continue;
    
    console.log(`Syncing stars for product ID: ${productId}`);
    
    try {
      const response = await fetch(`https://empty-truth-44e4.yonatanzewude.workers.dev/reviews/summary?productId=${productId}`);
      const data = await response.json();
      
      console.log(`Review data for product ${productId}:`, data);
      
      if (data.success && data.summary) {
        const { averageRating = 0, totalReviews = 0 } = data.summary;
        const stars = card.querySelectorAll('.star');
        
        console.log(`Product ${productId}: ${averageRating} stars, ${totalReviews} reviews`);
        
        // Always update stars based on review data
        stars.forEach((star, index) => {
          if (totalReviews > 0) {
            // Use actual rating from reviews
            if (index < Math.floor(averageRating)) {
              star.style.color = '#f59e0b'; // Gold color for filled stars
              star.textContent = '★';
            } else if (index === Math.floor(averageRating) && averageRating % 1 >= 0.5) {
              star.style.color = '#f59e0b'; // Half star (simplified as full)
              star.textContent = '★';
            } else {
              star.style.color = '#d1d5db'; // Gray color for empty stars
              star.textContent = '★';
            }
          } else {
            // If no reviews, show 0 stars (all gray) to be honest
            star.style.color = '#d1d5db';
            star.textContent = '★';
          }
        });
      } else {
        // If no review data available, show 0 stars
        const stars = card.querySelectorAll('.star');
        stars.forEach((star) => {
          star.style.color = '#d1d5db';
          star.textContent = '★';
        });
      }
    } catch (error) {
      console.log(`Could not sync stars for product ${productId}:`, error);
      // Show 0 stars on error to be safe
      const stars = card.querySelectorAll('.star');
      stars.forEach((star) => {
        star.style.color = '#d1d5db';
        star.textContent = '★';
      });
    }
  }
}

// Auto-run star sync when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Delay slightly to ensure all cards are rendered
  setTimeout(syncProductCardStars, 100);
});

// Also run when new content is loaded (e.g., pagination, AJAX)
window.addEventListener('load', function() {
  setTimeout(syncProductCardStars, 100);
});

// Make utilities available globally
window.debounce = debounce;
window.throttle = throttle;
window.formatMoney = formatMoney;
window.getUrlParameter = getUrlParameter;
window.Cookie = CookieUtil;
window.Storage = Storage;
window.Device = Device;
window.Animation = Animation;
window.syncProductCardStars = syncProductCardStars;